import React, { useState, useEffect } from 'react';
import { Table, Radio } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as FactorySvg } from '../../static/factory.svg';
import { ReactComponent as OverviewIcon } from '../../static/overview.svg';
import { ReactComponent as FinanceIcon } from '../../static/finance.svg';
import { ReactComponent as PerformanceIcon } from '../../static/performance.svg';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { fetchStationsSummaryList } from '../../modules/analytics';
import './sitesTable.css';

const energyRenderer = (val) => {
  const num = Number(val) || 0;
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)} MWh`;
  }
  return `${num.toFixed(1)} kWh`;
};

const currencyRenderer = (val) => `¥${(Number(val) || 0).toLocaleString()}`;
const percentRenderer = (val) => `${(Number(val) || 0).toFixed(2)}%`;

// Removed SiteNameCell component as it's no longer used

const getColumns = (view) => {
  switch (view) {
    case 'finance':
      return [
        {
          title: '',
          dataIndex: 'stationName',
          key: 'siteIcon',
          className: 'column-site-icon',
          render: (_, record) => <Icon component={FactorySvg} className="siteIcon" />
        },
        {
          title: 'Site Name',
          dataIndex: 'stationName',
          key: 'siteName',
          render: (text, record) => (
            <span className="siteNameOnly">
              <div>{text}</div>
              {record.stationSubtitle && <div className="subtitle">{record.stationSubtitle}</div>}
            </span>
          ),
        },
        { title: 'Period', dataIndex: 'period', key: 'period' },
        {
          title: 'Total Power Sold',
          dataIndex: 'totalPowerSold',
          key: 'totalPowerSold',
          render: currencyRenderer,
        },
        {
          title: 'Power Sold to Site',
          dataIndex: 'powerSoldToSite',
          key: 'powerSoldToSite',
          render: currencyRenderer,
        },
        {
          title: 'Power Sold to Grid',
          dataIndex: 'powerSoldToGrid',
          key: 'powerSoldToGrid',
          render: currencyRenderer,
        },
        { title: 'Solar Price Model', dataIndex: 'solarPriceModel', key: 'solarPriceModel' },
        {
          title: 'Self Utilization',
          dataIndex: 'selfUtilization',
          key: 'selfUtilization',
          render: percentRenderer,
        },
        { title: 'Utilization Hours', dataIndex: 'utilizationHours', key: 'utilizationHours' },
        {
          title: 'Estimated Cost Savings',
          dataIndex: 'estimatedCostSavings',
          key: 'estimatedCostSavings',
          render: currencyRenderer,
        },
        {
          title: 'Lifetime Estimated Cost Savings',
          dataIndex: 'lifetimeEstimatedCostSavings',
          key: 'lifetimeEstimatedCostSavings',
          render: currencyRenderer,
        },
      ];
    case 'performance':
      return [
        {
          title: '',
          dataIndex: 'stationName',
          key: 'siteIcon',
          className: 'column-site-icon',
          render: (_, record) => <Icon component={FactorySvg} className="siteIcon" />
        },
        {
          title: 'Site Name',
          dataIndex: 'stationName',
          key: 'siteName',
          render: (text, record) => (
            <span className="siteNameOnly">
              <div>{text}</div>
              {record.stationSubtitle && <div className="subtitle">{record.stationSubtitle}</div>}
            </span>
          ),
        },
        {
          title: 'Commercial Operation Date',
          dataIndex: 'commercialOperationDate',
          key: 'commercialOperationDate',
        },
        {
          title: 'Initial Performance Ratio',
          dataIndex: 'initialPerformanceRatio',
          key: 'initialPerformanceRatio',
          render: percentRenderer,
        },
        {
          title: 'Decay Factor',
          dataIndex: 'decayFactor',
          key: 'decayFactor',
          render: percentRenderer,
        },
        {
          title: 'Cumulative Decay',
          dataIndex: 'cumulativeDecay',
          key: 'cumulativeDecay',
          render: percentRenderer,
        },
        {
          title: 'Current Performance Ratio',
          dataIndex: 'currentPerformanceRatio',
          key: 'currentPerformanceRatio',
          render: percentRenderer,
        },
      ];
    case 'overview':
    default:
      return [
        {
          title: '',
          dataIndex: 'stationName',
          key: 'siteIcon',
          className: 'column-site-icon',
          render: (_, record) => <Icon component={FactorySvg} className="siteIcon" />
        },
        {
          title: 'Site Name',
          dataIndex: 'stationName',
          key: 'siteName',
          render: (text, record) => (
            <span className="siteNameOnly">
              <div>{text}</div>
              {record.stationSubtitle && <div className="subtitle">{record.stationSubtitle}</div>}
            </span>
          ),
        },
        {
          title: 'Daily Generation',
          dataIndex: 'dailyGeneration',
          key: 'dailyGeneration',
          render: energyRenderer,
        },
        {
          title: 'Monthly Generation',
          dataIndex: 'monthlyGeneration',
          key: 'monthlyGeneration',
          render: energyRenderer,
        },
        {
          title: 'Annual Generation',
          dataIndex: 'annualGeneration',
          key: 'annualGeneration',
          render: energyRenderer,
        },
        {
          title: 'Lifetime Generation',
          dataIndex: 'lifetimeGeneration',
          key: 'lifetimeGeneration',
          render: energyRenderer,
        },
        {
          title: 'Site Capacity',
          dataIndex: 'capacity',
          key: 'capacity',
          render: (v) => {
            return v != null && v !== '' ? `${v} MW` : null;
          }
        },
        {
          title: 'Commercial Operation Date',
          dataIndex: 'commercialOperationDate',
          key: 'commercialOperationDate',
        },
      ];
  }
};

const StationsTable = ({ stations = [] }) => {
  const dispatch = useDispatch();
  const stationSummary = useSelector(state => state.analytics.stationSummary || {});
  const [view, setView] = useState('overview');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const ids = stations
      .slice((page - 1) * pageSize, page * pageSize)
      .map((s) => s.stationId);
    if (ids.length > 0) {
      dispatch(fetchStationsSummaryList(ids));
    }
  }, [dispatch, page, stations]);

  const period = moment().format('YYYY-MM');
  const dataSource = stations.map((s) => {
    const summary = stationSummary[s.stationId] || {};
    return {
      ...s,
      dailyGeneration: summary.daily?.value,
      monthlyGeneration: summary.monthly?.value,
      annualGeneration: summary.yearly?.value,
      lifetimeGeneration: summary.lifetime,
      commercialOperationDate: s.commercialOperationDate
        ? moment(s.commercialOperationDate).format('YYYY-MM-DD')
        : '',
      period,
      ...summary,
    };
  });

  const columns = getColumns(view);
  return (
    <div className="stationsTableContainer">
      <Radio.Group className="tableControls" value={view} onChange={e => setView(e.target.value)}>
        <Radio.Button value="overview">
          <OverviewIcon className="tabIcon" />
          Overview
        </Radio.Button>
        <Radio.Button value="finance">
          <FinanceIcon className="tabIcon" />
          Finance
        </Radio.Button>
        <Radio.Button value="performance">
          <PerformanceIcon className="tabIcon" />
          Performance
        </Radio.Button>
      </Radio.Group>
      <Table
        components={{
          header: {
            row: (rowProps) => <tr {...rowProps} className="tableHeaderRow" />,
          },
        }}
        className="stationsTable"
        columns={columns}
        dataSource={dataSource}
        rowKey="stationId"
        pagination={{
          pageSize,
          onChange: setPage,
          showSizeChanger: false,
          showLessItems: true,
          showQuickJumper: false,
          showPrevNextJumpers: false,
          showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total}`,
          itemRender: (page, type, element) => {
            if (type === 'prev') return null;
            if (type === 'next') {
              return (
                <button className="ant-pagination-item ant-pagination-item-ellipsis pagination-next">
                  Next
                </button>
              );
            }
            return element;
          },
        }}
        rowClassName={() => 'uniformRow'}
      />
    </div>
  );
};

export default StationsTable;
